import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';
import Toast from './Toast';

// Text/free-form columns edited in place. jamaat_id and mumineen_house_count
// are handled separately below since they're numeric.
const TEXT_FIELDS = [
  { key: 'jamaat_name', label: 'Jamaat Name', width: 'min-w-[160px]' },
  { key: 'jamiat_name', label: 'Jamiat Name', width: 'min-w-[140px]' },
  { key: 'jamaat_type_name', label: 'Jamaat Type', width: 'min-w-[120px]' },
  { key: 'ilaqa', label: 'Ilaqa', width: 'min-w-[120px]' },
  { key: 'ilaqa_ummal', label: 'Ilaqa Ummal', width: 'min-w-[140px]' },
  { key: 'whatsapp_number', label: 'WhatsApp #', width: 'min-w-[130px]' },
  { key: 'ummal', label: 'Ummal', width: 'min-w-[140px]' },
  { key: 'notification_email', label: 'Notification Email', width: 'min-w-[180px]' },
  { key: 'notification_ummal_name', label: 'Notification Ummal Name', width: 'min-w-[180px]' },
];

const NUMBER_FIELDS = [
  { key: 'jamaat_id', label: 'Jamaat ID' },
  { key: 'mumineen_house_count', label: 'House Count' },
];

// SuperAdmin-only screen to bulk-edit the Jamiat/Jamaat master list (name,
// area, contact details) in one pass, replacing the current manual
// Excel-to-MongoDB workflow. Mirrors ManageSilatItems.jsx.
const ManageJamiats = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [records, setRecords] = useState([]); // original, as fetched
  const [edits, setEdits] = useState({}); // recordId -> { field: value }
  const [fieldErrors, setFieldErrors] = useState({}); // recordId -> { field: message }
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'success' });

  useEffect(() => {
    async function init() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
        return;
      }
      fetchRecords();
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/jamiat/adminList`, { withCredentials: true });
      const data = res.data?.data || [];
      setRecords(data);
      setEdits({});
      setFieldErrors({});
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      if (msg) setMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const getRowValue = (record, field) => {
    const edit = edits[record._id];
    if (edit && field in edit) return edit[field];
    return record[field] ?? '';
  };

  const validateField = (field, value) => {
    if (field === 'jamaat_name' || field === 'jamiat_name') {
      return !String(value ?? '').trim() ? 'Required' : '';
    }
    if (field === 'jamaat_id' || field === 'mumineen_house_count') {
      if (value === '' || value == null) return ''; // optional
      return isNaN(value) || Number(value) < 0 ? 'Invalid' : '';
    }
    if (field === 'notification_email') {
      const v = String(value ?? '').trim();
      if (!v) return ''; // optional
      return /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/.test(v) ? '' : 'Invalid email';
    }
    return '';
  };

  const handleCellChange = (record, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [record._id]: { ...prev[record._id], [field]: value },
    }));
    setFieldErrors((prev) => ({
      ...prev,
      [record._id]: { ...prev[record._id], [field]: validateField(field, value) },
    }));
  };

  const revertRow = (recordId) => {
    setEdits((prev) => {
      const copy = { ...prev };
      delete copy[recordId];
      return copy;
    });
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[recordId];
      return copy;
    });
  };

  const dirtyIds = Object.keys(edits).filter((id) => Object.keys(edits[id] || {}).length > 0);
  const hasErrors = Object.values(fieldErrors).some((rowErrs) =>
    Object.values(rowErrs || {}).some(Boolean)
  );
  const jamaatNameChangedIds = dirtyIds.filter((id) => 'jamaat_name' in (edits[id] || {}));

  const filteredRecords = useMemo(
    () =>
      records.filter((r) => {
        const term = search.toLowerCase();
        return (
          r.jamaat_name?.toLowerCase().includes(term) ||
          r.jamiat_name?.toLowerCase().includes(term) ||
          r.ilaqa?.toLowerCase().includes(term)
        );
      }),
    [records, search]
  );

  const handleSaveAll = async () => {
    if (dirtyIds.length === 0) return;
    if (hasErrors) {
      setMessage('Fix the highlighted fields before saving.');
      return;
    }

    const payload = dirtyIds.map((id) => {
      const record = records.find((r) => r._id === id);
      const row = { _id: id };
      [...TEXT_FIELDS, ...NUMBER_FIELDS].forEach(({ key }) => {
        row[key] = getRowValue(record, key);
      });
      return row;
    });

    setSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URI}/api/jamiat/bulkUpdate`,
        { items: payload },
        { withCredentials: true }
      );
      setToast({ message: `${payload.length} record${payload.length > 1 ? 's' : ''} updated successfully!`, type: 'success' });
      await fetchRecords();
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      setToast({ message: msg || 'Failed to save changes.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (record, field) => {
    const err = fieldErrors[record._id]?.[field];
    const changed = edits[record._id] && field in edits[record._id];
    return `w-full p-1.5 border rounded text-sm ${
      err ? 'border-red-500 bg-red-50' : changed ? 'border-amber-400 bg-amber-50' : 'border-gray-300'
    }`;
  };

  return (
    <div className="p-4 pb-28">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-fmb">Manage Jamiats</h1>
          <p className="text-sm text-gray-600">
            Edit Jamaat/Jamiat details directly — replaces the manual Excel-to-database refresh.
          </p>
        </div>
        <input
          type="text"
          placeholder="Search by Jamaat, Jamiat, or Ilaqa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="p-2 border border-gray-300 rounded-lg w-full md:w-72"
        />
      </div>

      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
        <strong>Jamaat Name</strong> is the key Umaal members' login uses to find their own commitments.
        Renaming it here is safe for existing commitment records (they're linked by ID, not name), but a
        member will stop matching to this Jamiat until their own login record is updated to the new name too.
        {jamaatNameChangedIds.length > 0 && (
          <span className="block mt-1 font-semibold text-amber-900">
            {jamaatNameChangedIds.length} row{jamaatNameChangedIds.length > 1 ? 's have' : ' has'} a Jamaat Name change pending in this batch.
          </span>
        )}
      </div>

      {message && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2 mb-3">{message}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <p>Loading Jamiat records...</p>
        </div>
      ) : filteredRecords.length === 0 ? (
        <p className="text-gray-600">No Jamiat records found.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {TEXT_FIELDS.slice(0, 2).map(({ key, label }) => (
                  <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</th>
                ))}
                {NUMBER_FIELDS.map(({ key, label }) => (
                  <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</th>
                ))}
                {TEXT_FIELDS.slice(2).map(({ key, label }) => (
                  <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.map((record) => {
                const isDirty = !!(edits[record._id] && Object.keys(edits[record._id]).length > 0);
                return (
                  <tr key={record._id} className={isDirty ? 'bg-amber-50/40' : ''}>
                    {TEXT_FIELDS.slice(0, 2).map(({ key, width }) => (
                      <td key={key} className={`px-4 py-2 ${width}`}>
                        <input
                          type="text"
                          value={getRowValue(record, key)}
                          onChange={(e) => handleCellChange(record, key, e.target.value)}
                          className={inputClass(record, key)}
                        />
                      </td>
                    ))}
                    {NUMBER_FIELDS.map(({ key }) => (
                      <td key={key} className="px-4 py-2 min-w-[110px]">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={getRowValue(record, key)}
                          onChange={(e) => handleCellChange(record, key, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className={inputClass(record, key)}
                        />
                      </td>
                    ))}
                    {TEXT_FIELDS.slice(2).map(({ key, width }) => (
                      <td key={key} className={`px-4 py-2 ${width}`}>
                        <input
                          type="text"
                          value={getRowValue(record, key)}
                          onChange={(e) => handleCellChange(record, key, e.target.value)}
                          className={inputClass(record, key)}
                        />
                      </td>
                    ))}
                    <td className="px-4 py-2">
                      {isDirty && (
                        <button
                          onClick={() => revertRow(record._id)}
                          className="text-xs text-gray-500 hover:text-red-500 underline"
                        >
                          Revert
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dirtyIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-yellow-100 shadow-lg flex justify-between items-center z-40">
          <span className="text-sm font-semibold">
            {dirtyIds.length} record{dirtyIds.length > 1 ? 's' : ''} changed
            {hasErrors && <span className="text-red-600 ml-2">Fix errors before saving</span>}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => { setEdits({}); setFieldErrors({}); }}
              className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Discard All
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || hasErrors}
              className={`px-4 py-2 rounded text-white text-sm font-medium ${saving || hasErrors ? 'bg-gray-400' : 'bg-fmb hover:bg-fmb-dark'}`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'success' })}
        />
      )}
    </div>
  );
};

export default ManageJamiats;
